import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { SYSTEMS, SYSTEM_LIST } from '../emulator/systems';
import type { SystemId } from '../emulator/types';
import {
  MAX_ROM_TITLE_LENGTH,
  deleteRom,
  formatBytes,
  renameRom,
  romHasLocalData,
  type RomMeta,
} from '../storage/romRepository';
import { deleteSavesForRom } from '../storage/saveRepository';
import { recordTombstone } from '../storage/libraryTombstones';
import { deleteCloudSavesForSha1, isCloudEnabled, requestSync } from '../storage/cloudSync';
import { downloadRomFiles, downloadSaveData } from '../storage/dataExport';
import { importSaveFiles } from '../storage/dataImport';
import { BRAND_ICON_SRC, BRAND_LINE_JA, BRAND_LOGO } from '../brand';
import { useStore } from '../state/store';
import { DropZone } from './DropZone';
import { estimateStorage, type StorageEstimate } from '../storage/db';

export function LibraryScreen() {
  const roms = useStore((s) => s.roms);
  const romsLoaded = useStore((s) => s.romsLoaded);
  const refreshRoms = useStore((s) => s.refreshRoms);
  const play = useStore((s) => s.play);
  const setView = useStore((s) => s.setView);
  const toast = useStore((s) => s.toast);

  const [filter, setFilter] = useState<SystemId | 'all'>('all');
  const [query, setQuery] = useState('');
  const [storage, setStorage] = useState<StorageEstimate | null>(null);
  const [renaming, setRenaming] = useState<RomMeta | null>(null);
  const [menuRom, setMenuRom] = useState<RomMeta | null>(null);

  useEffect(() => {
    void refreshRoms();
    void estimateStorage().then(setStorage);
  }, [refreshRoms]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roms.filter(
      (r) => (filter === 'all' || r.system === filter) && (!q || r.title.toLowerCase().includes(q)),
    );
  }, [roms, filter, query]);

  const counts = useMemo(() => {
    const map = new Map<SystemId, number>();
    for (const r of roms) map.set(r.system, (map.get(r.system) ?? 0) + 1);
    return map;
  }, [roms]);

  const handleRename = async (rom: RomMeta, title: string) => {
    try {
      await renameRom(rom.id, title);
      await refreshRoms();
      setRenaming(null);
      toast('表示名を変更しました', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    }
  };

  const handleDeleteRom = async (rom: RomMeta) => {
    if (
      !confirm(
        `「${rom.title}」をライブラリから削除しますか？\nこの端末のセーブも消えます。\nサーバーのライブラリ一覧からも削除されます（同期後）。`,
      )
    ) {
      return;
    }
    try {
      await recordTombstone(rom.sha1);
      await deleteRom(rom.id);
      await refreshRoms();
      void estimateStorage().then(setStorage);
      requestSync();
      toast(`${rom.title} を削除しました`, 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    }
    setMenuRom(null);
  };

  const handleDeleteSaves = async (rom: RomMeta) => {
    const psxNote =
      rom.system === 'psx'
        ? '\n※ PS1 のメモリーカードは他の PS1 ソフトが無いときだけ消します。'
        : '';
    if (!confirm(`「${rom.title}」のセーブデータ（SRAM・ステート）を削除しますか？${psxNote}`)) {
      return;
    }
    try {
      const n = await deleteSavesForRom(rom.id, rom.system);
      let cloud = 0;
      if (isCloudEnabled() && /^[0-9a-f]{40}$/.test(rom.sha1)) {
        try {
          cloud = await deleteCloudSavesForSha1(rom.sha1);
        } catch {
          /* サーバー失敗でもローカルは消えている */
        }
      }
      void estimateStorage().then(setStorage);
      toast(
        cloud > 0
          ? `セーブを削除しました（端末 ${n} / サーバー ${cloud}）`
          : `セーブを ${n} 件削除しました`,
        'success',
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    }
    setMenuRom(null);
  };

  const handleDownloadRom = async (rom: RomMeta) => {
    try {
      const n = await downloadRomFiles(rom);
      toast(`ROM を ${n} ファイルダウンロードしました`, 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    }
    setMenuRom(null);
  };

  const handleDownloadSaves = async (rom: RomMeta) => {
    try {
      const n = await downloadSaveData(rom);
      toast(`セーブを ${n} ファイルダウンロードしました`, 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    }
    setMenuRom(null);
  };

  const handleImportSaves = async (rom: RomMeta, fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    try {
      const r = await importSaveFiles(rom, [...fileList]);
      const parts: string[] = [];
      if (r.sram) parts.push(`ゲーム内セーブ ${r.sram}`);
      if (r.states) parts.push(`ステート ${r.states}`);
      const skip =
        r.skipped.length > 0 ? `（スキップ ${r.skipped.length}）` : '';
      toast(`${parts.join('、')} を取り込みました${skip}`, 'success');
      void requestSync();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    }
    setMenuRom(null);
  };

  return (
    <DropZone className="h-full">
      <div className="library-shell flex h-full flex-col">
        <header className="pt-safe-header shrink-0 border-b border-base-600/60 bg-[#07090f] px-4 pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <img
                src={BRAND_ICON_SRC}
                alt=""
                width={36}
                height={36}
                className="h-9 w-9 shrink-0 rounded-lg md:h-7 md:w-7 landscape:h-7 landscape:w-7"
                draggable={false}
              />
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                <h1 className="brand-mark text-xl font-semibold tracking-tight text-white" aria-label={BRAND_LOGO}>
                  EmYu<span className="text-accent">-Ba</span>
                </h1>
                <p className="truncate text-[0.7rem] text-slate-500">{BRAND_LINE_JA}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                className="btn-ghost !min-h-0 !rounded-lg !py-1.5 !text-xs md:hidden landscape:hidden"
                onClick={() => setView('home')}
              >
                Home
              </button>
              <button
                type="button"
                className="btn-ghost !min-h-0 !rounded-lg !py-1.5 !text-xs"
                onClick={() => setView('settings')}
              >
                設定
              </button>
            </div>
          </div>

          <div className="mt-3 flex gap-2">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="タイトルを検索"
              className="min-w-0 flex-1 rounded-xl border border-base-600/80 bg-base-900/80 px-3 py-2 text-sm outline-none transition-colors placeholder:text-slate-600 focus:border-accent/70"
            />
          </div>

          <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
            <Chip active={filter === 'all'} onClick={() => setFilter('all')}>
              すべて ({roms.length})
            </Chip>
            {SYSTEM_LIST.map((sys) => (
              <Chip key={sys.id} active={filter === sys.id} onClick={() => setFilter(sys.id)}>
                {sys.short} ({counts.get(sys.id) ?? 0})
              </Chip>
            ))}
          </div>

          {storage && (
            <p className="mt-2 font-mono text-[0.65rem] tracking-wide text-slate-600">
              STORAGE {formatBytes(storage.usage)}
              {storage.quota > 0 ? ` / ${formatBytes(storage.quota)}` : ''}
            </p>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] pt-4 md:pb-28 landscape:pb-28">
          {!romsLoaded ? (
            <p className="animate-fade-in py-16 text-center text-sm text-slate-500">読み込み中…</p>
          ) : filtered.length === 0 ? (
            <EmptyState hasRoms={roms.length > 0} />
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {filtered.map((rom, i) => (
                <RomCard
                  key={rom.id}
                  rom={rom}
                  index={i}
                  onPlay={() => {
                    if (!romHasLocalData(rom)) {
                      toast('ROM 本体がありません。同じファイルを追加してください', 'error');
                      return;
                    }
                    play(rom.id);
                  }}
                  onMenu={() => setMenuRom(rom)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {renaming && (
        <RenameDialog
          rom={renaming}
          onCancel={() => setRenaming(null)}
          onSave={(title) => void handleRename(renaming, title)}
        />
      )}

      {menuRom && (
        <RomActionSheet
          rom={menuRom}
          onClose={() => setMenuRom(null)}
          onRename={() => {
            setRenaming(menuRom);
            setMenuRom(null);
          }}
          onDownloadRom={() => void handleDownloadRom(menuRom)}
          onDownloadSaves={() => void handleDownloadSaves(menuRom)}
          onImportSaves={(files) => void handleImportSaves(menuRom, files)}
          onDeleteSaves={() => void handleDeleteSaves(menuRom)}
          onDeleteRom={() => void handleDeleteRom(menuRom)}
        />
      )}
    </DropZone>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-lg px-3 py-1.5 font-display text-[0.7rem] font-semibold tracking-wide transition-colors ${
        active
          ? 'bg-accent text-black shadow-[0_0_0_1px_rgba(74,222,128,0.35)]'
          : 'border border-base-600/80 bg-base-900/70 text-slate-400 hover:border-base-500 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

function RomCard({
  rom,
  index,
  onPlay,
  onMenu,
}: {
  rom: RomMeta;
  index: number;
  onPlay: () => void;
  onMenu: () => void;
}) {
  const sys = SYSTEMS[rom.system];
  const ready = romHasLocalData(rom);
  return (
    <li
      className="rom-cart group animate-cart-in"
      style={{ animationDelay: `${Math.min(index, 12) * 35}ms` }}
    >
      <button type="button" onClick={onPlay} className="block w-full text-left">
        <div
          className="relative flex items-center justify-center bg-black/50"
          style={{ aspectRatio: String(sys.aspect) }}
        >
          <span className="pointer-events-none absolute left-2 top-2 rounded border border-white/10 bg-black/55 px-1.5 py-0.5 font-display text-[0.55rem] font-semibold tracking-wider text-accent/90">
            {sys.short}
          </span>
          {rom.thumbnail ? (
            <img
              src={rom.thumbnail}
              alt=""
              className={`h-full w-full object-cover ${ready ? '' : 'opacity-40'}`}
              style={{ imageRendering: 'pixelated' }}
            />
          ) : (
            <span className="font-display text-2xl font-bold tracking-wider text-base-500/90">{sys.short}</span>
          )}
          {!ready && (
            <span className="absolute inset-x-2 bottom-2 rounded border border-amber-500/30 bg-black/80 px-1.5 py-0.5 text-center text-[0.6rem] font-medium text-amber-200">
              ROM 未投入
            </span>
          )}
        </div>
        <div className="border-t border-white/[0.04] p-2.5 pl-3.5">
          <p className="truncate text-sm font-medium text-slate-100">{rom.title}</p>
          <p className="mt-0.5 truncate font-mono text-[0.65rem] text-slate-500">
            {formatBytes(rom.size)}
            {rom.playCount > 0 && ` · ${rom.playCount} PLAY`}
            {!ready && ' · SYNC META'}
          </p>
        </div>
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMenu();
        }}
        aria-label={`${rom.title} の操作メニュー`}
        className="absolute right-1.5 top-1.5 rounded-md border border-white/10 bg-black/65 px-2.5 py-1 text-xs text-slate-300 transition-colors hover:border-accent/40 hover:text-white focus:text-white"
      >
        ⋯
      </button>
    </li>
  );
}

function RomActionSheet({
  rom,
  onClose,
  onRename,
  onDownloadRom,
  onDownloadSaves,
  onImportSaves,
  onDeleteSaves,
  onDeleteRom,
}: {
  rom: RomMeta;
  onClose: () => void;
  onRename: () => void;
  onDownloadRom: () => void;
  onDownloadSaves: () => void;
  onImportSaves: (files: FileList | null) => void;
  onDeleteSaves: () => void;
  onDeleteRom: () => void;
}) {
  const ready = romHasLocalData(rom);
  const importRef = useRef<HTMLInputElement>(null);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 pb-[calc(4.75rem+env(safe-area-inset-bottom,0px))] sm:items-center sm:pb-0 md:pb-0 landscape:pb-0">
      <button type="button" className="absolute inset-0 cursor-default" aria-label="閉じる" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm rounded-t-2xl border border-base-500/70 bg-gradient-to-b from-base-700 to-base-800 p-4 pb-4 shadow-2xl sm:rounded-2xl">
        <p className="truncate font-display text-sm font-semibold tracking-wide text-white">{rom.title}</p>
        <p className="mt-0.5 font-mono text-[0.65rem] text-accent/80">{SYSTEMS[rom.system].name}</p>
        <div className="mt-3 flex flex-col gap-2">
          <button type="button" className="btn-ghost w-full !justify-start" onClick={onRename}>
            表示名を変更
          </button>
          <button
            type="button"
            className="btn-ghost w-full !justify-start"
            disabled={!ready}
            onClick={onDownloadRom}
          >
            ROM をダウンロード
          </button>
          <button type="button" className="btn-ghost w-full !justify-start" onClick={onDownloadSaves}>
            セーブデータをダウンロード
          </button>
          <input
            ref={importRef}
            type="file"
            multiple
            accept=".srm,.sav,.sram,.bin,.mcd,.mcr,.state,.memcard"
            className="hidden"
            onChange={(e) => {
              onImportSaves(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="btn-ghost w-full !justify-start"
            onClick={() => importRef.current?.click()}
          >
            セーブデータを読み込む
          </button>
          <button type="button" className="btn-ghost w-full !justify-start text-amber-200" onClick={onDeleteSaves}>
            セーブデータだけ削除
          </button>
          <button type="button" className="btn-danger w-full !justify-start" onClick={onDeleteRom}>
            ライブラリから削除
          </button>
          <button type="button" className="btn-ghost w-full" onClick={onClose}>
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}

function RenameDialog({
  rom,
  onCancel,
  onSave,
}: {
  rom: RomMeta;
  onCancel: () => void;
  onSave: (title: string) => void;
}) {
  const [title, setTitle] = useState(rom.title);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const trimmed = title.trim();
  const invalid = trimmed.length === 0 || trimmed.length > MAX_ROM_TITLE_LENGTH;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-sm p-4">
        <p className="text-sm font-semibold">表示名を変更</p>
        <input
          ref={inputRef}
          value={title}
          maxLength={MAX_ROM_TITLE_LENGTH}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !invalid) onSave(trimmed);
            if (e.key === 'Escape') onCancel();
          }}
          className="mt-3 w-full rounded-xl border border-base-600 bg-base-900 px-3 py-2 text-sm outline-none focus:border-accent"
          aria-label="表示名"
        />
        <div className="mt-3 flex gap-2">
          <button type="button" className="btn-ghost flex-1" onClick={onCancel}>
            キャンセル
          </button>
          <button
            type="button"
            className="btn-primary flex-1"
            disabled={invalid}
            onClick={() => onSave(trimmed)}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ hasRoms }: { hasRoms: boolean }) {
  return (
    <div className="animate-fade-in py-16 text-center">
      <p className="brand-mark text-sm font-semibold tracking-wide text-slate-300">
        {hasRoms ? 'NO MATCH' : 'EMPTY SHELF'}
      </p>
      <p className="mt-2 text-sm text-slate-400">
        {hasRoms ? '条件に合う ROM がありません' : 'ROM がまだ登録されていません'}
      </p>
      {!hasRoms && (
        <p className="mx-auto mt-3 max-w-sm text-xs leading-relaxed text-slate-600">
          ファイルをこの画面にドラッグ&amp;ドロップするか、右下の「+ ROM を追加」から選択してください。
          ライブラリ一覧とセーブはサーバー同期されます。ROM 本体は端末内のみです。
        </p>
      )}
    </div>
  );
}
